import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getCustomerOrgAccounts } from "../../store/customer/customerSlice";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  outlinedButtonSx,
} from "../Compliance/AuditHub/theme";
import { autocompleteSlotProps } from "./formFieldStyles";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const asText = (value) => (value == null ? "" : String(value).trim());

const accountLabel = (account) => {
  const number = account?.accountNumber || "";
  const name = account?.accountName && account.accountName !== number ? account.accountName : "";
  return name ? `${name} (${number})` : number;
};

const accountsSummary = (selectedCount) => {
  if (!selectedCount) return "None selected";
  if (selectedCount === 1) return "1 Account";
  return `${selectedCount} Accounts`;
};

/** Same API shape as Add Customer, with camelCase / map-key fallbacks. */
const normalizeOrgAccounts = (payload) => {
  let data = payload?.data;
  if (!data || typeof data !== "object") return [];

  if (
    data.data &&
    typeof data.data === "object" &&
    !Array.isArray(data.data) &&
    data.account_number == null &&
    data.accountNumber == null
  ) {
    data = data.data;
  }

  const entries = Array.isArray(data)
    ? data.map((acc, index) => [String(index), acc])
    : Object.entries(data);

  return entries
    .filter(([key, acc]) => {
      if (!acc || typeof acc !== "object") return false;
      if (key === "status" || key === "message" || key === "error") return false;
      return true;
    })
    .map(([key, acc]) => {
      const accountNumber = asText(
        acc.account_number ?? acc.accountNumber ?? (/^\d{12}$/.test(key) ? key : "")
      );
      return {
        accountNumber,
        accountName: asText(acc.account_name ?? acc.accountName),
        status: asText(acc.status),
        company: asText(acc.company),
        payerAccountId: asText(acc.payer_account_id ?? acc.payerAccountId),
        payerAccountName: asText(acc.payer_account_name ?? acc.payerAccountName),
      };
    })
    .filter((acc) => acc.accountNumber);
};

const normalizeLinkedAccounts = (accounts) => {
  if (!Array.isArray(accounts)) return [];
  return accounts
    .map((acc) => ({
      accountNumber: asText(acc.accountNumber ?? acc.account_number),
      accountName: asText(acc.accountName ?? acc.account_name),
      status: asText(acc.status) || "ONBOARDED",
      company: asText(acc.company),
      payerAccountId: asText(acc.payerAccountId ?? acc.payer_account_id),
      payerAccountName: asText(acc.payerAccountName ?? acc.payer_account_name),
    }))
    .filter((acc) => acc.accountNumber);
};

const mergeCatalog = (...lists) => {
  const map = new Map();
  lists.flat().forEach((acc) => {
    if (!acc?.accountNumber) return;
    const prev = map.get(acc.accountNumber);
    if (!prev) {
      map.set(acc.accountNumber, acc);
      return;
    }
    map.set(acc.accountNumber, {
      ...prev,
      ...acc,
      accountName: acc.accountName || prev.accountName,
      payerAccountId: acc.payerAccountId || prev.payerAccountId,
      payerAccountName: acc.payerAccountName || prev.payerAccountName,
      company: acc.company || prev.company,
      status: acc.status || prev.status,
    });
  });
  return Array.from(map.values());
};

const accordionSx = {
  border: `1px solid ${AH_BORDER}`,
  borderRadius: "8px !important",
  backgroundColor: AH_WHITE,
  "&:before": { display: "none" },
  overflow: "hidden",
};

const accordionSummarySx = {
  minHeight: 48,
  px: 1.5,
  "& .MuiAccordionSummary-content": {
    my: 1,
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    pr: 1,
  },
};

const accordionDetailsSx = {
  px: 1.5,
  pt: 0,
  pb: 1.5,
  borderTop: `1px solid ${AH_BORDER}`,
  backgroundColor: AH_WHITE,
};

const payerBadgeSx = {
  height: 20,
  fontSize: 11,
  fontWeight: 700,
  backgroundColor: "#fff4eb",
  border: `1px solid ${AH_ACCENT}55`,
  color: AH_TEXT,
  "& .MuiChip-label": { px: 0.75 },
};

const selectedChipSx = (isPayer) => ({
  height: 28,
  fontSize: 12,
  fontWeight: 500,
  backgroundColor: isPayer ? "#fff4eb" : AH_WHITE,
  border: `1px solid ${isPayer ? `${AH_ACCENT}55` : AH_BORDER}`,
  color: AH_TEXT,
  maxWidth: "100%",
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

/** Compact chips inside the Payer Autocomplete input. */
const payerInputChipSx = {
  height: 22,
  maxWidth: "100%",
  fontSize: 11,
  fontWeight: 500,
  fontFamily: "monospace",
  backgroundColor: AH_WHITE,
  border: `1px solid ${AH_BORDER}`,
  color: AH_TEXT,
  "& .MuiChip-label": {
    px: 0.75,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .MuiChip-deleteIcon": {
    fontSize: 14,
    color: AH_MUTED,
    "&:hover": { color: AH_TEXT },
  },
};

const selectAllChipSx = (active) => ({
  height: 36,
  minHeight: 36,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
  cursor: "pointer",
  backgroundColor: active ? "#fff4eb" : AH_WHITE,
  border: `1px solid ${AH_ACCENT}`,
  color: AH_ACCENT,
  "&:hover": {
    backgroundColor: "#fff4eb",
    borderColor: AH_ACCENT_HOVER,
    color: AH_ACCENT_HOVER,
  },
  "&.Mui-disabled": {
    opacity: 0.5,
    borderColor: AH_BORDER,
    color: AH_MUTED,
    backgroundColor: AH_WHITE,
  },
});

/**
 * 1) Top payer dropdown scopes which accounts appear below (does not add to Selected accounts).
 * 2) Single payee dropdown lists linked payees for scoped payers, plus each scoped payer
 *    itself as a selectable option (Payer chip). Selecting a payer there adds it to Selected accounts.
 * Payer/payee removals from Selected accounts stay independent of each other.
 */
const PayerPayeeAccountsField = ({
  id = "payerAccountNumbers",
  label = "AWS Payer / Payee Accounts",
  placeholder = "Select accounts...",
  hint,
  required = false,
  payerOptions,
  linkedAccounts = [],
  payerAccountNumbers,
  payeeAccountNumbers,
  /** Payers explicitly checked in the Payee dropdown (Selected Accounts "Payer:" chips). */
  payeeUiPayerNumbers: payeeUiPayerNumbersProp,
  onPayersChange,
  onPayeesChange,
  /** Optional: receives { "<payer>": ["<payee>", ...] } for the current UI selection. */
  onAccountsMapChange,
  /** When set, limit payers/payees to this customer-authorized account boundary. */
  allowedAccountNumbers,
  fieldSx,
}) => {
  const dispatch = useDispatch();
  const {
    isFetchingCustomerOrgAccounts,
    customerOrgAccountsSuccess,
    customerOrgAccountsError,
  } = useSelector((state) => state.customer);

  /** Top dropdown — scopes payee options; also owns form payerAccountNumbers (API map keys). */
  const [scopedPayerNumbers, setScopedPayerNumbers] = useState([]);
  /**
   * Payers the user explicitly checked in the Payee dropdown (Selected Accounts chips).
   * Hydrated when API values include the payer id under its own key.
   */
  const [payeeUiSelectedPayerNumbers, setPayeeUiSelectedPayerNumbers] = useState(() =>
    Array.isArray(payeeUiPayerNumbersProp) ? [...payeeUiPayerNumbersProp] : []
  );
  const scopeHydratedRef = useRef(false);

  // Keep local Selected-payer chips in sync with form (edit hydrate / atomic commits).
  useEffect(() => {
    if (!Array.isArray(payeeUiPayerNumbersProp)) return;
    setPayeeUiSelectedPayerNumbers((prev) => {
      const next = payeeUiPayerNumbersProp.map((id) => asText(id)).filter((id) => /^\d{12}$/.test(id));
      const prevKey = [...(prev || [])].sort().join(",");
      const nextKey = [...next].sort().join(",");
      return prevKey === nextKey ? prev : next;
    });
  }, [payeeUiPayerNumbersProp]);

  const orgAccounts = useMemo(
    () => normalizeOrgAccounts(customerOrgAccountsSuccess),
    [customerOrgAccountsSuccess]
  );

  const storeAccounts = useMemo(
    () => normalizeLinkedAccounts(linkedAccounts),
    [linkedAccounts]
  );

  const accountCatalog = useMemo(
    () => mergeCatalog(orgAccounts, storeAccounts),
    [orgAccounts, storeAccounts]
  );

  const accountByNumber = useMemo(() => {
    const map = new Map();
    accountCatalog.forEach((acc) => map.set(acc.accountNumber, acc));
    return map;
  }, [accountCatalog]);

  const allowedSet = useMemo(() => {
    if (!Array.isArray(allowedAccountNumbers)) return null;
    return new Set(
      allowedAccountNumbers.map((id) => asText(id)).filter((id) => /^\d{12}$/.test(id))
    );
  }, [allowedAccountNumbers]);

  const payerSelectOptions = useMemo(() => {
    const map = new Map();
    const add = (number, name = "") => {
      const idValue = asText(number);
      if (!/^\d{12}$/.test(idValue)) return;
      const prev = map.get(idValue);
      const accountName = asText(name);
      if (!prev) {
        map.set(idValue, { accountNumber: idValue, accountName });
        return;
      }
      if (!prev.accountName && accountName) {
        map.set(idValue, { accountNumber: idValue, accountName });
      }
    };
    (payerOptions || []).forEach((account) => add(account.accountNumber, account.accountName));
    storeAccounts.forEach((account) => add(account.accountNumber, account.accountName));
    orgAccounts.forEach((account) => {
      if (account.payerAccountId) add(account.payerAccountId, account.payerAccountName);
      if (account.accountNumber && account.accountNumber === account.payerAccountId) {
        add(account.accountNumber, account.accountName || account.payerAccountName);
      }
    });
    (scopedPayerNumbers || []).forEach((number) => add(number, ""));
    (payerAccountNumbers || []).forEach((number) => add(number, ""));

    let options = Array.from(map.values());
    if (allowedSet) {
      const payersWithAllowedPayee = new Set();
      accountCatalog.forEach((acc) => {
        if (allowedSet.has(acc.accountNumber) && acc.payerAccountId) {
          payersWithAllowedPayee.add(acc.payerAccountId);
        }
      });
      options = options.filter(
        (account) =>
          scopedPayerNumbers.includes(account.accountNumber) ||
          (payerAccountNumbers || []).includes(account.accountNumber) ||
          allowedSet.has(account.accountNumber) ||
          payersWithAllowedPayee.has(account.accountNumber)
      );
    }
    return options.sort((a, b) => {
      const left = (a.accountName || a.accountNumber).toLowerCase();
      const right = (b.accountName || b.accountNumber).toLowerCase();
      return left.localeCompare(right);
    });
  }, [
    payerOptions,
    storeAccounts,
    orgAccounts,
    allowedSet,
    accountCatalog,
    scopedPayerNumbers,
    payerAccountNumbers,
  ]);

  const scopedPayerSet = useMemo(
    () => new Set(scopedPayerNumbers),
    [scopedPayerNumbers]
  );

  const selectedScopedPayers = useMemo(
    () => payerSelectOptions.filter((account) => scopedPayerSet.has(account.accountNumber)),
    [payerSelectOptions, scopedPayerSet]
  );

  /**
   * Payee dropdown options: scoped payers (optional chip selection) + linked payees.
   * API hydrate selects only payees; payers appear as Selected chips only if the user picks them here.
   */
  const payeeDropdownOptions = useMemo(() => {
    if (!scopedPayerNumbers.length) return [];
    const options = [];
    const seen = new Set();

    scopedPayerNumbers.forEach((payerId) => {
      if (seen.has(`payer:${payerId}`)) return;
      seen.add(`payer:${payerId}`);
      const fromCatalog = accountByNumber.get(payerId);
      const fromPayerOpts = payerSelectOptions.find((a) => a.accountNumber === payerId);
      options.push({
        accountNumber: payerId,
        accountName: fromPayerOpts?.accountName || fromCatalog?.accountName || "",
        payerAccountId: payerId,
        payerAccountName: fromPayerOpts?.accountName || fromCatalog?.payerAccountName || "",
        role: "payer",
      });
    });

    accountCatalog.forEach((acc) => {
      if (!scopedPayerSet.has(acc.payerAccountId)) return;
      if (scopedPayerSet.has(acc.accountNumber)) return;
      if (allowedSet && !allowedSet.has(acc.accountNumber)) return;
      if (seen.has(`payee:${acc.accountNumber}`)) return;
      seen.add(`payee:${acc.accountNumber}`);
      options.push({
        ...acc,
        role: "payee",
      });
    });

    // Keep selected payees visible even if catalog briefly empty
    (payeeAccountNumbers || []).forEach((number) => {
      if (seen.has(`payee:${number}`) || seen.has(`payer:${number}`)) return;
      const fromCatalog = accountByNumber.get(number);
      if (fromCatalog?.payerAccountId && !scopedPayerSet.has(fromCatalog.payerAccountId)) return;
      seen.add(`payee:${number}`);
      options.push({
        accountNumber: number,
        accountName: fromCatalog?.accountName || "",
        payerAccountId: fromCatalog?.payerAccountId || "",
        payerAccountName: fromCatalog?.payerAccountName || "",
        role: "payee",
      });
    });

    return options.sort((a, b) => {
      if (a.role !== b.role) return a.role === "payer" ? -1 : 1;
      if (a.role === "payee" && a.payerAccountId !== b.payerAccountId) {
        return String(a.payerAccountId).localeCompare(String(b.payerAccountId));
      }
      return (a.accountName || a.accountNumber).localeCompare(b.accountName || b.accountNumber);
    });
  }, [
    scopedPayerNumbers,
    scopedPayerSet,
    accountCatalog,
    accountByNumber,
    payerSelectOptions,
    allowedSet,
    payeeAccountNumbers,
  ]);

  const selectedPayeeDropdownValue = useMemo(() => {
    const byKey = new Map(
      payeeDropdownOptions.map((opt) => [`${opt.role}:${opt.accountNumber}`, opt])
    );
    const selected = [];
    const seen = new Set();
    const pushSelected = (option) => {
      const key = `${option.role}:${option.accountNumber}`;
      if (seen.has(key)) return;
      seen.add(key);
      selected.push(option);
    };

    // Only payers the user explicitly chose in this dropdown (not all API/top payers).
    (payeeUiSelectedPayerNumbers || []).forEach((number) => {
      if (!scopedPayerSet.has(number)) return;
      pushSelected(
        byKey.get(`payer:${number}`) || {
          accountNumber: number,
          accountName: accountByNumber.get(number)?.accountName || "",
          payerAccountId: number,
          role: "payer",
        }
      );
    });

    (payeeAccountNumbers || []).forEach((number) => {
      if ((payeeUiSelectedPayerNumbers || []).includes(number)) return;
      if ((payerAccountNumbers || []).includes(number) && scopedPayerSet.has(number)) return;
      pushSelected(
        byKey.get(`payee:${number}`) || {
          accountNumber: number,
          accountName: accountByNumber.get(number)?.accountName || "",
          payerAccountId: accountByNumber.get(number)?.payerAccountId || "",
          role: "payee",
        }
      );
    });
    return selected;
  }, [
    payeeDropdownOptions,
    payeeUiSelectedPayerNumbers,
    payeeAccountNumbers,
    payerAccountNumbers,
    accountByNumber,
    scopedPayerSet,
  ]);

  const selectionSignature = useMemo(() => {
    const payers = [...(payerAccountNumbers || [])].map(String).sort().join(",");
    const payees = [...(payeeAccountNumbers || [])].map(String).sort().join(",");
    return `${payers}|${payees}`;
  }, [payerAccountNumbers, payeeAccountNumbers]);
  const prevSelectionSignatureRef = useRef("|");

  // Re-open hydrate when selection arrives, clears, or payer keys change (e.g. demote linked payees).
  useEffect(() => {
    const prev = prevSelectionSignatureRef.current || "|";
    const next = selectionSignature || "|";
    const prevPayers = prev.split("|")[0] || "";
    const nextPayers = next.split("|")[0] || "";
    const wasEmpty = prev === "|";
    const isEmpty = next === "|";
    if (isEmpty || (wasEmpty && !isEmpty) || prevPayers !== nextPayers) {
      scopeHydratedRef.current = false;
    }
    prevSelectionSignatureRef.current = next;
  }, [selectionSignature]);

  // Hydrate top payer scope from saved selection once the catalog is ready (edit / reopen).
  // When form already has payer keys, trust them — do not re-expand removed payers from payee owners.
  useEffect(() => {
    if (scopeHydratedRef.current) return;
    const hasSelection =
      (payerAccountNumbers || []).length > 0 || (payeeAccountNumbers || []).length > 0;
    if (!hasSelection) return;
    if (!customerOrgAccountsSuccess && !accountCatalog.length) return;

    const next = new Set();
    const savedPayers = (payerAccountNumbers || [])
      .map((id) => String(id || "").trim())
      .filter((id) => /^\d{12}$/.test(id));

    if (savedPayers.length) {
      savedPayers.forEach((id) => next.add(id));
    } else {
      // Create / payee-only hydrate: widen scope from payee linkage.
      (payeeAccountNumbers || []).forEach((id) => {
        const number = String(id || "").trim();
        if (!/^\d{12}$/.test(number)) return;
        const payer = asText(accountByNumber.get(number)?.payerAccountId);
        if (/^\d{12}$/.test(payer) && payer !== number) next.add(payer);
      });
      if (!next.size) {
        const only = asText((payeeAccountNumbers || [])[0]);
        if (/^\d{12}$/.test(only)) next.add(only);
      }
    }
    setScopedPayerNumbers([...next]);
    scopeHydratedRef.current = true;
  }, [
    customerOrgAccountsSuccess,
    accountCatalog.length,
    payerAccountNumbers,
    payeeAccountNumbers,
    accountByNumber,
    selectionSignature,
  ]);

  /**
   * Emit payers, payees, and the wire map together so Edit save cannot rebuild a stale map.
   * Map keys = current payer scope; values = selected payees grouped by known payer linkage.
   */
  const commitAccountSelection = (nextPayers, nextPayees, nextPayeeUiPayers = payeeUiSelectedPayerNumbers) => {
    const payers = [
      ...new Set(
        (nextPayers || [])
          .map((id) => asText(id))
          .filter((id) => /^\d{12}$/.test(id))
      ),
    ];
    const payees = [
      ...new Set(
        (nextPayees || [])
          .map((id) => asText(id))
          .filter((id) => /^\d{12}$/.test(id) && !payers.includes(id))
      ),
    ];
    const payeeUiPayers = [
      ...new Set(
        (nextPayeeUiPayers || [])
          .map((id) => asText(id))
          .filter((id) => /^\d{12}$/.test(id) && payers.includes(id))
      ),
    ];

    setPayeeUiSelectedPayerNumbers(payeeUiPayers);

    const map = {};
    payers.forEach((payer) => {
      map[payer] = [];
    });
    // User-selected "Payer:" chips belong in that payer's values array on the wire.
    payeeUiPayers.forEach((payer) => {
      if (!map[payer]) return;
      if (!map[payer].includes(payer)) map[payer].push(payer);
    });
    payees.forEach((payee) => {
      const owner = asText(accountByNumber.get(payee)?.payerAccountId);
      const key =
        (owner && map[owner] ? owner : null) ||
        (payers.length === 1 ? payers[0] : null) ||
        payers[0];
      if (!key || !map[key]) return;
      if (!map[key].includes(payee)) map[key].push(payee);
    });
    const cleaned = {};
    Object.entries(map).forEach(([payer, list]) => {
      if (list.length) cleaned[payer] = list;
    });

    if (typeof onAccountsMapChange === "function") {
      // Single atomic form update: payers + payees + wire map + payer chips.
      onAccountsMapChange(cleaned, {
        payerAccountNumbers: payers,
        payeeAccountNumbers: payees,
        payeeUiPayerNumbers: payeeUiPayers,
      });
    } else {
      onPayersChange(payers);
      onPayeesChange(payees);
    }
  };

  /** Top Payer dropdown owns form payer ids (API map keys); prune payees to that scope. */
  const syncPayersAndPrunePayees = (nextScope) => {
    const scope = new Set(nextScope);
    const nextPayers = (nextScope || []).filter((id) => /^\d{12}$/.test(String(id || "").trim()));
    const catalogReady = accountByNumber.size > 0;
    const nextPayees = (payeeAccountNumbers || []).filter((id) => {
      if (scope.has(id)) return false;
      const payer = asText(accountByNumber.get(id)?.payerAccountId);
      if (/^\d{12}$/.test(payer)) return scope.has(payer);
      return !catalogReady;
    });
    commitAccountSelection(
      nextPayers,
      nextPayees,
      (payeeUiSelectedPayerNumbers || []).filter((id) => scope.has(id))
    );
  };

  const handleScopedPayersChange = (_event, value) => {
    const next = value.map((account) => account.accountNumber);
    setScopedPayerNumbers(next);
    syncPayersAndPrunePayees(next);
  };

  const allPayersSelected =
    payerSelectOptions.length > 0 &&
    selectedScopedPayers.length === payerSelectOptions.length;

  const toggleSelectAllPayers = () => {
    if (!payerSelectOptions.length) return;
    if (allPayersSelected) {
      setScopedPayerNumbers([]);
      syncPayersAndPrunePayees([]);
      return;
    }
    const next = payerSelectOptions.map((account) => account.accountNumber);
    setScopedPayerNumbers(next);
    syncPayersAndPrunePayees(next);
  };

  const applyPayeeSelection = (options) => {
    const payersFromPayeeUi = [];
    const payees = [];
    options.forEach((option) => {
      const number = asText(option?.accountNumber);
      if (!/^\d{12}$/.test(number)) return;
      if (option.role === "payer") payersFromPayeeUi.push(number);
      else payees.push(number);
    });
    commitAccountSelection(
      [...new Set([...(scopedPayerNumbers || []), ...payersFromPayeeUi])],
      payees,
      payersFromPayeeUi
    );
  };

  const allPayeesSelected =
    payeeDropdownOptions.length > 0 &&
    selectedPayeeDropdownValue.length === payeeDropdownOptions.length &&
    payeeDropdownOptions.every((option) =>
      selectedPayeeDropdownValue.some(
        (selected) =>
          selected.accountNumber === option.accountNumber && selected.role === option.role
      )
    );

  const handlePayeeDropdownChange = (_event, value) => {
    applyPayeeSelection(value);
  };

  const toggleSelectAllPayees = () => {
    if (!payeeDropdownOptions.length) return;
    if (allPayeesSelected) {
      commitAccountSelection([...(scopedPayerNumbers || [])], [], []);
      return;
    }
    applyPayeeSelection(payeeDropdownOptions);
  };

  const removeSelectedAccount = (accountNumber, role) => {
    if (role === "payer") {
      commitAccountSelection(
        payerAccountNumbers || scopedPayerNumbers,
        payeeAccountNumbers,
        (payeeUiSelectedPayerNumbers || []).filter((id) => id !== accountNumber)
      );
      return;
    }
    commitAccountSelection(
      payerAccountNumbers || scopedPayerNumbers,
      (payeeAccountNumbers || []).filter((id) => id !== accountNumber),
      payeeUiSelectedPayerNumbers
    );
  };

  const orgLoaded = Boolean(customerOrgAccountsSuccess);
  const orgLoading = isFetchingCustomerOrgAccounts && !orgLoaded;
  const showPayeeStep = scopedPayerNumbers.length > 0;
  // Match accordion header to Selected Accounts (deduped), not payer+payee raw lengths.
  const summary = accountsSummary(selectedPayeeDropdownValue.length);
  const panelId = `${id}-panel`;
  const headerId = `${id}-header`;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Accordion disableGutters elevation={0} sx={accordionSx}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: AH_MUTED }} />}
          aria-controls={panelId}
          id={headerId}
          sx={accordionSummarySx}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }}>
              {label}
              {required ? (
                <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }} aria-hidden="true">
                  *
                </Box>
              ) : null}
            </Typography>
            {hint ? (
              <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                {hint}
              </Typography>
            ) : null}
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_MUTED, flexShrink: 0 }}>
            {summary}
          </Typography>
        </AccordionSummary>

        <AccordionDetails id={panelId} sx={accordionDetailsSx}>
          <Box sx={{ pt: 1.5, display: "grid", gap: 2 }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_TEXT }}>
                  Payer Accounts
                </Typography>
                <Typography sx={{ fontSize: 12, color: AH_MUTED, flexShrink: 0 }}>
                  {selectedScopedPayers.length} selected
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "flex-start" },
                  gap: 1,
                }}
              >
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  id={`${id}-scope`}
                  options={payerSelectOptions}
                  value={selectedScopedPayers}
                  onChange={handleScopedPayersChange}
                  getOptionLabel={accountLabel}
                  isOptionEqualToValue={(option, value) =>
                    option.accountNumber === value.accountNumber
                  }
                  loading={orgLoading && payerSelectOptions.length === 0}
                  slotProps={autocompleteSlotProps}
                  noOptionsText={
                    orgLoading && payerSelectOptions.length === 0
                      ? "Loading payer accounts…"
                      : "No payer accounts found"
                  }
                  sx={{ flex: 1, minWidth: 0 }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box
                        component="li"
                        key={key}
                        {...optionProps}
                        sx={{
                          display: "flex !important",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          py: "6px !important",
                        }}
                      >
                        <Checkbox
                          icon={checkboxIcon}
                          checkedIcon={checkboxCheckedIcon}
                          checked={selected}
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            p: 0.25,
                            color: AH_MUTED,
                            "&.Mui-checked": { color: AH_ACCENT },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            color: AH_TEXT,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {accountLabel(option)}
                        </Typography>
                      </Box>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          {...tagProps}
                          size="small"
                          label={option.accountNumber}
                          title={accountLabel(option)}
                          sx={payerInputChipSx}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        selectedScopedPayers.length ? "" : "Search account number or name"
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {orgLoading && payerSelectOptions.length === 0 ? (
                              <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  )}
                />
                <Chip
                  clickable
                  disabled={!payerSelectOptions.length}
                  label="Select All"
                  onClick={toggleSelectAllPayers}
                  aria-pressed={allPayersSelected}
                  aria-label={
                    allPayersSelected ? "Deselect all payer accounts" : "Select All payer accounts"
                  }
                  sx={selectAllChipSx(allPayersSelected)}
                />
              </Box>
              <Typography sx={{ mt: 0.75, fontSize: 12, color: AH_MUTED }}>
                Choose payer accounts to load linked payees, then select the accounts to link to
                this program.
              </Typography>
            </Box>

            {!showPayeeStep && orgLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: AH_MUTED }} />
                <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                  Loading organization accounts…
                </Typography>
              </Box>
            ) : null}

            {showPayeeStep ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_TEXT }}>
                    Payee Accounts
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: AH_MUTED, flexShrink: 0 }}>
                    {selectedPayeeDropdownValue.length} selected
                  </Typography>
                </Box>

                {customerOrgAccountsError ? (
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#b91c1c", flex: "1 1 200px" }}>
                      {typeof customerOrgAccountsError === "string"
                        ? customerOrgAccountsError
                        : "Unable to load organization accounts."}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={isFetchingCustomerOrgAccounts}
                      onClick={() => dispatch(getCustomerOrgAccounts())}
                      sx={{ ...outlinedButtonSx, height: 32, minHeight: 32, px: 1.5 }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "flex-start" },
                    gap: 1,
                  }}
                >
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    id={id}
                    options={payeeDropdownOptions}
                    value={selectedPayeeDropdownValue}
                    onChange={handlePayeeDropdownChange}
                    getOptionLabel={(option) => {
                      if (option.role === "payer") return `${accountLabel(option)} Payer`;
                      const payer = asText(option.payerAccountId);
                      return payer
                        ? `${accountLabel(option)} Payer ${payer}`
                        : accountLabel(option);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.accountNumber === value.accountNumber && option.role === value.role
                    }
                    loading={orgLoading}
                    slotProps={autocompleteSlotProps}
                    noOptionsText={
                      orgLoading ? "Loading payee accounts…" : "No accounts for the selected payers"
                    }
                    sx={{ flex: 1, minWidth: 0 }}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Box
                          component="li"
                          key={key}
                          {...optionProps}
                          sx={{
                            display: "flex !important",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            py: "6px !important",
                          }}
                        >
                          <Checkbox
                            icon={checkboxIcon}
                            checkedIcon={checkboxCheckedIcon}
                            checked={selected}
                            tabIndex={-1}
                            disableRipple
                            sx={{
                              p: 0.25,
                              color: AH_MUTED,
                              "&.Mui-checked": { color: AH_ACCENT },
                            }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  color: AH_TEXT,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  minWidth: 0,
                                }}
                              >
                                {accountLabel(option)}
                              </Typography>
                              {option.role === "payer" ? (
                                <Chip
                                  size="small"
                                  label="Payer"
                                  sx={{ ...payerBadgeSx, flexShrink: 0 }}
                                />
                              ) : null}
                            </Box>
                            {option.role === "payee" && option.payerAccountId ? (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: AH_MUTED,
                                  fontFamily: "monospace",
                                  mt: 0.15,
                                }}
                              >
                                Payer {option.payerAccountId}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      );
                    }}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search account number or name"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {orgLoading ? (
                                <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    )}
                  />
                  <Chip
                    clickable
                    disabled={!payeeDropdownOptions.length}
                    label="Select All"
                    onClick={toggleSelectAllPayees}
                    aria-pressed={allPayeesSelected}
                    aria-label={allPayeesSelected ? "Deselect all payee accounts" : "Select All payee accounts"}
                    sx={selectAllChipSx(allPayeesSelected)}
                  />
                </Box>

                {selectedPayeeDropdownValue.length ? (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_TEXT }}>
                        Selected Accounts
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: AH_MUTED, flexShrink: 0 }}>
                        {selectedPayeeDropdownValue.length} selected
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        border: `1px solid ${AH_BORDER}`,
                        borderRadius: "8px",
                        backgroundColor: AH_TRACK,
                        p: { xs: 1.25, sm: 1.5 },
                      }}
                    >
                      {selectedPayeeDropdownValue.map((option) => {
                        const isPayer = option.role === "payer";
                        return (
                          <Chip
                            key={`${option.role}-${option.accountNumber}`}
                            size="small"
                            label={
                              isPayer
                                ? `Payer: ${option.accountNumber}`
                                : option.accountNumber
                            }
                            title={
                              isPayer
                                ? `Payer: ${accountLabel(option)}`
                                : accountLabel(option)
                            }
                            onDelete={() =>
                              removeSelectedAccount(option.accountNumber, option.role)
                            }
                            sx={selectedChipSx(isPayer)}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                Select one or more payer accounts to assign linked payees.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PayerPayeeAccountsField;
